// Pure text-highlighting helpers for in-chat search: one for plain-text snippets in the result
// list, one for the message bubbles themselves (which render through react-markdown).
//
// No React, no DOM — kept side-effect-free so both can be reasoned about and unit-tested on their
// own.

/** Escape a user-typed term so it is matched literally, never as a pattern. */
function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Split `text` into alternating {text, match} parts around every case-insensitive occurrence of
 * `term`. Empty parts are dropped; a blank term or text returns a single non-matching part.
 *
 * Note this deliberately does NOT use the `parts.map(p => regex.test(p))` shape found elsewhere in
 * the app: `test` on a /g regex advances `lastIndex`, so alternating parts get misclassified. Here
 * the split is driven by an explicit index walk, so a part's match state is known, not re-derived.
 */
export function splitHighlightParts(text, term) {
  const source = String(text ?? "");
  const needle = String(term ?? "").trim();
  if (!source) return [];
  if (!needle) return [{ text: source, match: false }];

  const haystack = source.toLowerCase();
  const lowerNeedle = needle.toLowerCase();
  const parts = [];
  let cursor = 0;
  for (;;) {
    const at = haystack.indexOf(lowerNeedle, cursor);
    if (at < 0) break;
    if (at > cursor) parts.push({ text: source.slice(cursor, at), match: false });
    parts.push({ text: source.slice(at, at + needle.length), match: true });
    cursor = at + needle.length;
  }
  if (cursor < source.length) parts.push({ text: source.slice(cursor), match: false });
  return parts.length ? parts : [{ text: source, match: false }];
}

/** Elements whose text is markup-ish or already interactive — never split those. */
const HIGHLIGHT_SKIP_TAGS = new Set(["code", "pre", "a"]);

/**
 * A rehype plugin that wraps every occurrence of `term` in a <mark class="chat-search-mark">.
 *
 * This runs on the parsed tree rather than injecting markdown into the source string (the
 * `**bold**` trick `emphasizeMentions` uses): a term containing `*`, `_`, `[` or a backtick would
 * otherwise corrupt the document, and a term that happens to sit inside a code span or a link URL
 * would be rewritten where it must not be. Skipping code/pre/a subtrees keeps those verbatim.
 *
 * Returns undefined (a no-op transform) for a blank term, so callers can pass the plugin
 * unconditionally.
 */
export function rehypeHighlightTerm(term) {
  const needle = String(term ?? "").trim();
  return () => (tree) => {
    if (!needle || !tree) return;
    visit(tree);
  };

  function visit(node) {
    const children = Array.isArray(node?.children) ? node.children : null;
    if (!children) return;

    const next = [];
    let changed = false;
    for (const child of children) {
      if (child?.type === "element") {
        if (!HIGHLIGHT_SKIP_TAGS.has(String(child.tagName || "").toLowerCase())) visit(child);
        next.push(child);
        continue;
      }
      if (child?.type !== "text" || !child.value) {
        next.push(child);
        continue;
      }
      const parts = splitHighlightParts(child.value, needle);
      if (!parts.some((part) => part.match)) {
        next.push(child);
        continue;
      }
      changed = true;
      for (const part of parts) {
        if (!part.text) continue;
        next.push(
          part.match
            ? {
                type: "element",
                tagName: "mark",
                properties: { className: ["chat-search-mark"] },
                children: [{ type: "text", value: part.text }],
              }
            : { type: "text", value: part.text },
        );
      }
    }
    if (changed) node.children = next;
  }
}
